/**
 * Blueprint.tsx — Blueprint Planner heatmap + gap list.
 *
 * Shows a 15-slot reference-architecture heatmap with fit status and
 * a dependency-ordered gap list below. V6-4-6 / ADR-0040.
 *
 * Route: /w/:slug/app/blueprint
 */

import { useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Alert,
  Badge,
  Button,
  Card,
  EmptyState,
  PageHeader,
  Spinner,
} from '@aigovops/design-system';
import { useWorkspace } from '../lib/workspace';
import { useWorkspaceQuery } from '../hooks/useWorkspaceQuery';
import { queryKeys } from '../lib/query-keys';
import { getBlueprint, rerunBlueprint, exportBlueprintPackage, type BlueprintRoadmapItem } from '../lib/api';

const FIT_LABELS: Record<string, string> = {
  native: 'Native',
  partial: 'Partial',
  substitute: 'Substitute',
  missing: 'Missing',
};

/** DS-token color per fit status. */
const FIT_COLORS: Record<string, string> = {
  native: 'var(--success)',
  partial: 'var(--warning)',
  substitute: 'var(--warning)',
  missing: 'var(--critical)',
};

/** DS-token color per gap severity. */
const SEVERITY_COLORS: Record<string, string> = {
  critical: 'var(--critical)',
  high: 'var(--critical)',
  medium: 'var(--warning)',
  low: 'var(--text-muted)',
};

const EFFORT_LABELS: Record<string, string> = {
  xs: 'XS',
  s: 'S',
  m: 'M',
  l: 'L',
  xl: 'XL',
};

/** Semantic pill — a styled inline span using DS color tokens. */
function Pill({ label, color }: { label: string; color: string }) {
  return (
    <span
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        borderRadius: 'var(--radius-chip)',
        border: `1px solid ${color}`,
        color,
        fontFamily: 'var(--font-ui)',
        fontSize: 'var(--font-size-3xs)',
        fontWeight: 500,
        padding: '1px 8px',
        whiteSpace: 'nowrap',
        textTransform: 'capitalize',
      }}
    >
      {label}
    </span>
  );
}

/** Color for heatmap cell background, using DS tokens. */
function fitCellStyle(fit: string): React.CSSProperties {
  const colorMap: Record<string, string> = {
    native: 'var(--success-subtle)',
    partial: 'var(--warning-subtle)',
    substitute: 'var(--warning-subtle)',
    missing: 'var(--critical-subtle)',
  };
  return {
    background: colorMap[fit] ?? 'var(--surface)',
    borderRadius: 'var(--radius-m)',
    padding: 'var(--space-3)',
    border: '1px solid var(--border)',
  };
}

export function Blueprint() {
  const { workspace, role } = useWorkspace();
  const slug = workspace.slug;
  const navigate = useNavigate();
  const canManage = role === 'owner' || role === 'admin';
  const [rerunning, setRerunning] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [exportError, setExportError] = useState<string | null>(null);
  const [exportResult, setExportResult] = useState<{ version_id: string; verify_url: string } | null>(null);

  const q = useWorkspaceQuery(
    (s) => queryKeys.blueprint(s),
    (s) => getBlueprint(s),
  );
  const blueprint = q.data?.blueprint;

  const handleRerun = useCallback(async () => {
    setRerunning(true);
    try {
      await rerunBlueprint(slug);
      q.refetch();
    } finally {
      setRerunning(false);
    }
  }, [slug, q]);

  const handleExport = useCallback(async () => {
    setExporting(true);
    setExportError(null);
    try {
      const result = await exportBlueprintPackage(slug);
      setExportResult({ version_id: result.version_id, verify_url: result.verify_url });
    } catch (err) {
      setExportError(err instanceof Error ? err.message : 'Export failed.');
    } finally {
      setExporting(false);
    }
  }, [slug]);

  const handleDeliverToAssessor = useCallback(() => {
    if (!exportResult) return;
    navigate(
      `/w/${encodeURIComponent(slug)}/app/settings/assessors?snapshot_id=${encodeURIComponent(exportResult.version_id)}`
    );
  }, [exportResult, navigate, slug]);

  if (q.loading) return <Spinner label="Loading blueprint" />;
  if (q.error) return <Alert variant="error">{String(q.error)}</Alert>;
  if (!blueprint) return <EmptyState title="No blueprint data" description="Unable to load blueprint information." />;

  const { heatmap, gaps, roadmap, summary, inventory, computed_at } = blueprint;

  return (
    <div style={{ display: 'grid', gap: 'var(--space-4)' }}>
      <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', flexWrap: 'wrap', gap: 'var(--space-2)' }}>
        <PageHeader
          title="Blueprint Planner"
          subtitle={`Reference architecture fit across ${summary.total_slots} capability slots. Last computed ${new Date(computed_at).toLocaleString()}.`}
        />
        <div style={{ display: 'flex', gap: 'var(--space-2)' }}>
          <Button
            variant="secondary"
            onClick={handleRerun}
            disabled={rerunning}
          >
            {rerunning ? 'Recomputing...' : 'Recompute'}
          </Button>
          {canManage ? (
            <Button
              variant="secondary"
              onClick={handleExport}
              disabled={exporting}
            >
              {exporting ? 'Exporting...' : 'Export signed package'}
            </Button>
          ) : null}
        </div>
      </div>

      {exportError ? <Alert variant="error">{exportError}</Alert> : null}
      {exportResult ? (
        <Alert variant="success">
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 'var(--space-3)', flexWrap: 'wrap' }}>
            <span>
              Package exported. Verify at{' '}
              <a href={exportResult.verify_url} target="_blank" rel="noreferrer">
                {exportResult.verify_url}
              </a>
              .
            </span>
            {canManage ? (
              <Button variant="secondary" compact onClick={handleDeliverToAssessor}>
                Deliver to assessor portal
              </Button>
            ) : null}
          </div>
        </Alert>
      ) : null}

      {/* Summary row */}
      <div style={{ display: 'flex', gap: 'var(--space-3)', flexWrap: 'wrap' }}>
        <SummaryTile label="Native" count={summary.native} color="var(--success)" />
        <SummaryTile label="Partial" count={summary.partial} color="var(--warning)" />
        <SummaryTile label="Substitute" count={summary.substitute} color="var(--warning)" />
        <SummaryTile label="Missing" count={summary.missing} color="var(--critical)" />
      </div>

      {/* Inventory stats */}
      <Card>
        <div style={{ padding: 'var(--space-3)', color: 'var(--text-muted)', fontSize: 'var(--text-sm)' }}>
          Scanned: {inventory.connectors_scanned} connectors, {inventory.discovered_tools_scanned} discovered
          tools, {inventory.ci_submissions_scanned} CI submissions ({inventory.items_count} inventory items total)
        </div>
      </Card>

      {/* Heatmap grid */}
      <Card>
        <div style={{ padding: 'var(--space-4)' }}>
          <h3 style={{ margin: '0 0 var(--space-3) 0', color: 'var(--text)', fontSize: 'var(--text-base)' }}>
            Capability Heatmap
          </h3>
          <div
            style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fill, minmax(220px, 1fr))',
              gap: 'var(--space-2)',
            }}
          >
            {heatmap.map((slot) => (
              <div key={slot.slot_id} style={fitCellStyle(slot.fit)}>
                <div style={{
                  fontWeight: 600,
                  fontSize: 'var(--text-sm)',
                  color: 'var(--text)',
                  marginBottom: 'var(--space-1)',
                  lineHeight: 1.3,
                }}>
                  {formatSlotId(slot.slot_id)}
                </div>
                <div style={{
                  fontSize: 'var(--text-xs)',
                  color: 'var(--text-muted)',
                  marginBottom: 'var(--space-2)',
                  lineHeight: 1.4,
                }}>
                  {slot.role.length > 80 ? slot.role.slice(0, 80) + '...' : slot.role}
                </div>
                <div style={{ display: 'flex', gap: 'var(--space-1)', alignItems: 'center' }}>
                  <Pill
                    label={FIT_LABELS[slot.fit] ?? slot.fit}
                    color={FIT_COLORS[slot.fit] ?? 'var(--text-muted)'}
                  />
                  <Badge>{EFFORT_LABELS[slot.effort_band] ?? slot.effort_band}</Badge>
                </div>
              </div>
            ))}
          </div>
        </div>
      </Card>

      {/* Gaps list */}
      <Card>
        <div style={{ padding: 'var(--space-4)' }}>
          <h3 style={{ margin: '0 0 var(--space-3) 0', color: 'var(--text)', fontSize: 'var(--text-base)' }}>
            Gaps ({gaps.length})
          </h3>
          {gaps.length === 0 ? (
            <EmptyState
              title="No gaps"
              description="All reference architecture slots are natively filled."
            />
          ) : (
            <div style={{ display: 'grid', gap: 'var(--space-2)' }}>
              {gaps.map((gap) => (
                <div
                  key={gap.slot_id}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    padding: 'var(--space-3)',
                    background: 'var(--surface)',
                    borderRadius: 'var(--radius-m)',
                    border: '1px solid var(--border)',
                  }}
                >
                  <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-2)' }}>
                    <Pill
                      label={gap.severity}
                      color={SEVERITY_COLORS[gap.severity] ?? 'var(--text-muted)'}
                    />
                    <span style={{ fontWeight: 500, color: 'var(--text)' }}>
                      {formatSlotId(gap.slot_id)}
                    </span>
                  </div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-2)' }}>
                    {gap.blocks.length > 0 && (
                      <span style={{ fontSize: 'var(--text-xs)', color: 'var(--text-dim)' }}>
                        Blocks {gap.blocks.length} slot{gap.blocks.length > 1 ? 's' : ''}
                      </span>
                    )}
                    <Badge>{EFFORT_LABELS[gap.effort_band] ?? gap.effort_band}</Badge>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </Card>

      {/* Roadmap: priority-ordered action items (gaps x systems), sorted
          by regulatory deadline then severity then dependency order. */}
      <Card>
        <div style={{ padding: 'var(--space-4)' }}>
          <h3 style={{ margin: '0 0 var(--space-3) 0', color: 'var(--text)', fontSize: 'var(--text-base)' }}>
            Roadmap ({roadmap.length})
          </h3>
          {roadmap.length === 0 ? (
            <EmptyState
              title="No roadmap items"
              description="Nothing is prioritized — every slot is natively filled."
            />
          ) : (
            <div style={{ display: 'grid', gap: 'var(--space-2)' }}>
              {roadmap.map((item, idx) => (
                <RoadmapRow key={item.id} item={item} rank={idx + 1} />
              ))}
            </div>
          )}
        </div>
      </Card>
    </div>
  );
}

// ---- Helpers ----

function RoadmapRow({ item, rank }: { item: BlueprintRoadmapItem; rank: number }) {
  return (
    <div
      style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        gap: 'var(--space-3)',
        padding: 'var(--space-3)',
        background: 'var(--surface)',
        borderRadius: 'var(--radius-m)',
        border: '1px solid var(--border)',
        flexWrap: 'wrap',
      }}
    >
      <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-3)' }}>
        <span
          style={{
            display: 'inline-flex',
            alignItems: 'center',
            justifyContent: 'center',
            minWidth: 24,
            height: 24,
            borderRadius: 'var(--radius-chip)',
            background: 'var(--surface-elevated)',
            border: '1px solid var(--border)',
            color: 'var(--text-muted)',
            fontSize: 'var(--font-size-3xs)',
            fontWeight: 600,
          }}
        >
          {rank}
        </span>
        <span style={{ fontWeight: 500, color: 'var(--text)' }}>
          {formatSlotId(item.gap_slot_id)}
        </span>
        {item.blocked_artifact ? (
          <span style={{ fontSize: 'var(--text-xs)', color: 'var(--text-dim)' }}>
            Blocks {formatSlotId(item.blocked_artifact)}
          </span>
        ) : null}
      </div>
      <div style={{ fontSize: 'var(--text-xs)', color: 'var(--text-muted)' }}>
        {item.regulatory_deadline
          ? `Due ${new Date(item.regulatory_deadline).toLocaleDateString()}`
          : 'No regulatory deadline'}
      </div>
    </div>
  );
}

function SummaryTile({ label, count, color }: { label: string; count: number; color: string }) {
  return (
    <Card>
      <div style={{
        padding: 'var(--space-3)',
        textAlign: 'center',
        minWidth: 100,
      }}>
        <div style={{ fontSize: 'var(--text-2xl)', fontWeight: 700, color }}>
          {count}
        </div>
        <div style={{ fontSize: 'var(--text-sm)', color: 'var(--text-muted)' }}>{label}</div>
      </div>
    </Card>
  );
}

/** Convert snake_case slot_id to a readable label. */
function formatSlotId(id: string): string {
  return id
    .split('_')
    .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
    .join(' ');
}
